/**
 * Email Outcome Modal — recorded for a pending INITIAL_CONTACT (EMAIL channel).
 */
import { useState } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getErrorMessage } from "../../../utils/api";

const OUTCOMES = [
  { value: "POSITIVE_REPLY", label: "Pozitivan odgovor", bonus: "+2 boda", needsDate: true },
  { value: "CALL_REQUESTED", label: "Zatražen poziv", bonus: "+2 boda", needsDate: true },
  { value: "MEETING_REQUESTED", label: "Zatražen sastanak", bonus: "+4 boda", needsMeeting: true },
  { value: "NO_REPLY", label: "Nema odgovora", bonus: "0 bodova", needsDate: false },
  { value: "NEGATIVE_REPLY", label: "Negativan odgovor", bonus: "0 bodova", needsDate: false },
  { value: "DO_NOT_CONTACT", label: "Ne kontaktirati", bonus: "0 bodova", needsDate: false },
  { value: "BOUNCE", label: "Neispravna e-mail adresa (bounce)", bonus: "0 bodova", warning: true },
];

export default function EmailOutcomeModal({ open, activity, onClose, onSaved }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [nextActionDate, setNextActionDate] = useState("");
  const [meetingDatetime, setMeetingDatetime] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open || !activity) return null;

  const selected = OUTCOMES.find((o) => o.value === outcome);

  const submit = async () => {
    if (!outcome) {
      toast.error("Odaberite ishod");
      return;
    }
    if (selected?.needsDate && !nextActionDate) {
      toast.error("Datum sljedećeg koraka je obavezan");
      return;
    }
    if (selected?.needsMeeting && !meetingDatetime) {
      toast.error("Datum sastanka je obavezan");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email_outcome: outcome,
        note: note || null,
      };
      if (selected?.needsDate) payload.next_action_date = nextActionDate;
      if (selected?.needsMeeting) {
        payload.meeting_datetime = meetingDatetime;
        if (contactName) payload.contact_name = contactName;
        if (contactPhone) payload.contact_phone = contactPhone;
      }
      await apiClient.patch(
        `/companies/${activity.company_id}/activities/${activity.id}/email-outcome`,
        payload,
      );
      toast.success("Ishod e-maila spremljen");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="email-outcome-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail size={18} className="text-indigo-600" /> Ishod poslanog e-maila
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {OUTCOMES.map((o) => (
            <button
              key={o.value}
              onClick={() => setOutcome(o.value)}
              data-testid={`email-outcome-${o.value}`}
              className={`w-full text-left p-3 rounded-lg border flex items-center justify-between transition-colors ${
                outcome === o.value
                  ? "border-indigo-500 bg-indigo-50 border-2"
                  : "border-gray-200 hover:border-gray-300"
              } ${o.warning ? "" : ""}`}
            >
              <div>
                <div className="font-medium text-gray-900">{o.label}</div>
                {o.warning && <div className="text-xs text-red-600">⚠ Označit će firmu kao "Provjeri kontakt podatke"</div>}
              </div>
              <span className="text-xs text-gray-500">{o.bonus}</span>
            </button>
          ))}

          {selected?.needsDate && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Datum sljedećeg koraka *</label>
              <input
                type="date"
                value={nextActionDate}
                onChange={(e) => setNextActionDate(e.target.value)}
                data-testid="email-outcome-next-date"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          )}

          {selected?.needsMeeting && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Datum i vrijeme sastanka *</label>
                <input
                  type="datetime-local"
                  value={meetingDatetime}
                  onChange={(e) => setMeetingDatetime(e.target.value)}
                  data-testid="email-outcome-meeting-datetime"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder="Kontakt osoba"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  placeholder="Telefon"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Napomena</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="email-outcome-note"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              placeholder="Kratak sažetak odgovora..."
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            Odustani
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="email-outcome-submit"
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? "Spremam..." : "Spremi ishod"}
          </button>
        </div>
      </div>
    </div>
  );
}
